import { useEffect, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../../../models/chart_model";
import { renderHourTick } from "../../../../../../../components/recharts/components";
import { R_CHART_TYPES } from "../../../../../../DemoState/const";
import loader from "../../../../../../../components/recharts/loader.gif";
import { getMonthFromDate } from "../../../../../../../extensions/date";
import { normalizeDisplayLabelWithoutUndefined } from "../../../../../../../utility/displayText";

interface SummaryData {
  date: string;
  month: string;
  oar: number;
  gna_cost: number;
  gna_quantum: number;
  tgna_cost: number;
  tgna_quantum: number;
  optimal_gna: number;
  total_cost: number;
  slot: number;
}

export default function DailyVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading,
  selectedTargetDate,
  data,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  loading: boolean;
  selectedTargetDate: any[];
  data: SummaryData[];
}) {
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);

  const chartSuffixes = [
    {
      suffix: "tgna_quantum",
      displayName: "Optimal TGNA Utilisation",
      stackSuffix: "tgna_quantum",
      stroke: "rgba(255, 194, 158, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "gna_quantum",
      displayName: "Optimal GNA Utilisation",
      stackSuffix: "gna_quantum",
      stroke: "rgba(241, 147, 92, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "optimal_gna",
      displayName: "Optimal GNA",
      stackSuffix: "optimal_gna",
      stroke: "rgba(52, 101, 109, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
      isDash: true
    },
    {
      suffix: "oar",
      displayName: "Total Open Access Requirement",
      stackSuffix: "oar",
      stroke: "rgba(17, 141, 255, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
    },
  ];

  const [selectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
    chartSuffixes[2],
    chartSuffixes[3],
  ]);
  const [selectedChartType] = useState(R_CHART_TYPES[1]);

  useEffect(() => {


    if (!data || data.length === 0) {
      setChartData([]);
      setChartKeys([]);
      return;
    }

    // Check if this is the new scenario-based data format
    if (data[0] && 'years' in data[0]) {


      // Transform: Each row = { year, Scenario_0_solar, Scenario_0_wind, ... }
      const transformedData: any[] = [];
      const allYears = new Set<number>();

      // Collect all years
      data.forEach((scenario: any) => {
        scenario.years?.forEach((yearData: any) => allYears.add(yearData.year));
      });

      // Create chart row for each year
      Array.from(allYears).sort().forEach((year) => {
        const row: any = { year: year.toString() };

        data.forEach((scenario: any) => {
          const scenarioLabel = `Scenario_${scenario.scenario_number}`;
          const yearData = scenario.years?.find((y: any) => y.year === year);

          if (yearData) {
            yearData.sources?.forEach((source: any) => {
              const key = `${scenarioLabel}_${source.source}`;
              row[key] = parseFloat(source.generation_gwh || "0");
            });
          }
        });

        transformedData.push(row);
      });

      // Extract keys
      const keys: string[] = [];
      if (transformedData.length > 0) {
        Object.keys(transformedData[0]).forEach(key => {
          if (key !== 'year') keys.push(key);
        });
      }



      setChartData(transformedData);
      setChartKeys(keys);
      return;
    }

    // Old logic for backward compatibility
    let filteredData = data.map((item) => ({
      ...item,
      month: getMonthFromDate(item.date),
    }));

    if (selectedTargetDate.length > 0) {
      const types = selectedTargetDate.map((t: any) => t.value.split("-").reverse().join("-") ?? t);
      filteredData = filteredData.filter((row) => types.includes(row.date));
    }

    const par: ChartDataParams = {
      keys: ["date", "slot", "month"],
      tableKeys: ["date", "slot", "month"],
      groupBy: [""],
      volumeKey: ``,
      aggregationKeys: ["gna_quantum", "tgna_quantum", "optimal_gna", "oar"],
      averageKeys: [],
      averageDuplicationKeys: ["date", "slot", "month"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, selectedBlock, selectedTargetDate]);

  const createChartObject = (
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    fill: string,
    yAxisId: string
  ) => {
    return {
      dataKey: suffix,
      name: displayName,
      legendColor: fill === "none" ? stroke : fill,
      type: chartType,
      unit: unit,
      stroke: stroke,
      fill: fill,
      yAxisId: yAxisId,
      stackId: stackSuffix || undefined,
    };
  };

  function getLegends() {
    // Check if we have scenario-based data
    if (chartKeys.length > 0 && chartKeys[0].includes('Scenario')) {
      // New scenario-based format: keys like "Scenario 0_solar", "Scenario 1_wind"
      const sourceColors: any = {
        solar: '#FDB462',
        wind: '#80B1D3',
        hydro: '#FCBFBF',
        thermal: '#BEBADA',
        biomass: '#8DD3C7',
        bagasse: '#FFFFB3',
        gas: '#FCCDE5',
        nuclear: '#D9D9D9',
        hybrid: '#BC80BD',
        small_hydro: '#CCEBC5',
        bess: '#FFED6F'
      };

      return chartKeys.map((key: string) => {
        // Extract source name and scenario from key like "Scenario_0_solar"
        const parts = key.split('_');
        const scenarioNum = parts[1]; // "0", "1", etc.
        const source = parts.slice(2).join('_'); // "solar", "wind", etc.
        const stroke = sourceColors[source] || '#000000';
        const displayName = `Scenario ${scenarioNum} ${source
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')}`;
        const stackSuffix = `${parts[0]}_${scenarioNum}`; // "Scenario_0", "Scenario_1"

        return createChartObject(
          key,           // dataKey
          displayName,   // name
          stackSuffix,   // stackId
          ChartType.Bar, // type
          "GWh",        // unit  
          stroke,        // stroke
          stroke,        // fill
          "left"        // yAxisId
        );
      });
    }

    // Old format
    return selectedMetrics.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
      }) =>
        chartKeys.map(
          (key) => {
            const dataKey = key ? `${key}_${suffix}` : suffix;
            const name = `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`;
            return createChartObject(
              dataKey,
              name,
              stackSuffix,
              chartType,
              unit,
              stroke,
              stroke,
              yAxisId
            ) as any;
          }
        )
    );
  }

  return (
    <>
      {loading ? (
        <div className="w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : chartData.length === 0 ? (
        <div className="w-full bg-white flex items-center justify-center h-[400px]">
          <p className="text-gray-500 !text-sm">Please Select Scenarios to View Data</p>
        </div>
      ) : (
        <ReMixChart
          chartHeight="calc(100% - 50px)"
          data={chartData}
          showBrush={chartData.length > 96 * 2 ? true : undefined}
          brushStart={BrushStart.Start}
          isTimeSlot={false}
          onlyTitle={true}
          legends={getLegends()}
          yAxisLabel={chartKeys.length > 0 && chartKeys[0].includes('Scenario') ? "GwH" : "MW"}
          nameGap={40}
          xDataKey={chartKeys.length > 0 && chartKeys[0].includes('Scenario') ? "year" : "date"}
          top={10}
          bottom={30}
          xTick={(value: any, index: any) => {
            return renderHourTick(value, 10);
          }}
          showToolbox={false}
        />
      )}
    </>
  );
}
